/**
 * Liga a grade ao painel: linha escolhida vira valores no formulário.
 *
 * Substitui o `master-detail-panel` do módulo antigo, e a diferença de tamanho
 * — 842 linhas contra estas — não é otimização: **aquele componente preenchia
 * campo procurando `<input>` no DOM** porque não havia `FormGroup`. Com o
 * modelo de valores, preencher é escrever num mapa, e todo o resto some junto:
 * os cinco eventos sintéticos por campo, os quatro `setTimeout` escalonados
 * removendo classes de erro, e o `color: rgba(0,0,0,.85) !important` que
 * deixava o texto ilegível nos temas escuros.
 *
 * Ver `master-detail-binder.ts` para o porquê de não ser uma cópia.
 */
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ScreenDocument } from '../model/screen-schema';
import { MasterDetailService } from '../services/master-detail.service';
import { ScreenForm, ScreenFormService } from './screen-form.service';
import { valuesForPanels } from './master-detail-binder';

@Injectable()
export class MasterDetailBinderService {

  private readonly masterDetail = inject(MasterDetailService);
  private readonly forms = inject(ScreenFormService);
  private readonly destroyRef = inject(DestroyRef);

  private document: ScreenDocument | null = null;
  private screenForm: ScreenForm | null = null;
  private listening = false;

  /**
   * Passa a escutar a grade desta tela.
   *
   * Chamado uma vez por tela. A inscrição usa `takeUntilDestroyed` porque o
   * `MasterDetailService` é `providedIn: 'root'` — sem isso, a tela fechada
   * continuaria recebendo linha da grade da tela seguinte.
   */
  start(document: ScreenDocument, screenForm: ScreenForm): void {
    this.document = document;
    this.screenForm = screenForm;

    if (this.listening) {
      return;
    }

    this.listening = true;

    this.masterDetail.selectedRow$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => this.apply(state.selectedRow, state.targetPanelIds));
  }

  private apply(row: unknown, panelIds: readonly string[]): void {
    const document = this.document;
    const screenForm = this.screenForm;

    if (document === null || screenForm === null || row === null) {
      return;
    }

    const valores = valuesForPanels(document, panelIds, row);

    for (const [nodeId, valor] of Object.entries(valores)) {
      // `writeValue` escreve com `emitEvent: false`, como toda escrita
      // programática do motor: preencher o detalhe não deve disparar o
      // `onChange` de cada campo preenchido.
      this.forms.writeValue(screenForm, nodeId, valor);
    }
  }
}
